/**
 * 조건 칩이 **접힌 채로 적는 말** — 고르개(`HubSelect`)와 조건 팝오버(`HubConditionPopover`)가 같이 쓴다.
 *
 * 🔑 여럿 고른 칸은 「첫 이름 외 N」으로 줄인다 — 소재 찾기 툴바와 같은 말투다.
 * 🚨 칩마다 따로 줄이지 마라 — 같은 줄에 선 칩들의 말투가 갈린다.
 * ⚠️ `'use client'` 를 붙이지 않는다 — 순수 변환이다.
 */

/** 이름을 찾을 한 칸 — 값과 보이는 이름 */
export type HubSummaryOption = { value: string; label: string };

/**
 * 고른 것을 한 줄로.
 *
 * @param value 하나 고를 때는 문자열, 여럿이면 문자열 목록
 * @param options 이름을 찾을 선택지 — 없는 값은 값 그대로 적는다
 * @param empty 아무것도 안 골랐을 때의 글자(「전체」)
 */
export function summarizeSelection(
  value: string | readonly string[],
  options: readonly HubSummaryOption[],
  empty: string,
): string {
  const labelOf = (one: string) => options.find((option) => option.value === one)?.label ?? one;
  if (Array.isArray(value)) {
    if (value.length === 0) return empty;
    return value.length === 1 ? labelOf(value[0]) : `${labelOf(value[0])} 외 ${value.length - 1}`;
  }
  const one = String(value ?? '');
  return one ? labelOf(one) : empty;
}

/**
 * 여럿 고르는 칸에서 **누른 칸 하나**를 켜고 끈다 — `HubSelect multiple` 은 누른 값 하나를 준다.
 *
 * @param list 지금 고른 값들
 * @param value 누른 값
 * @returns 새 목록 — 차례는 고른 차례를 지킨다
 */
export function toggleInList(list: Iterable<string>, value: string): string[] {
  const current = [...list];
  return current.includes(value) ? current.filter((one) => one !== value) : [...current, value];
}

/**
 * 묶음 팝오버의 접힌 말 — 걸린 수가 있으면 그 수, 없으면 빈 글자.
 *
 * @param count 걸린 조건 수
 * @param empty 걸린 것이 없을 때의 글자
 */
export function summarizeCount(count: number, empty: string): string {
  return count > 0 ? String(count) : empty;
}
